import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarX, Mail } from 'lucide-react';
import { useAuth } from '../../auth/useAuth';

interface AccessExpiredProps {
  /** ISO timestamp the server reported, when it gave one. */
  expiredAt?: string;
}

/**
 * Shown when the API refuses every request because the reader's access window
 * has closed.
 *
 * This replaces the generic "could not load" panel that used to appear on every
 * tab. An expired subscriber is not looking at a broken portal, and telling
 * them it is broken sends them to support with the wrong question.
 */
export const AccessExpired: React.FC<AccessExpiredProps> = ({ expiredAt }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-GB';

  const when = expiredAt
    ? new Date(expiredAt).toLocaleDateString(locale, {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="max-w-lg w-full bg-white border border-[#EDE6DE] rounded-xl p-8 text-center shadow-[0_1px_2px_rgba(17,24,39,.06)]">
        <div className="w-14 h-14 rounded-full bg-[#C5922E]/10 flex items-center justify-center mx-auto mb-5">
          <CalendarX size={26} className="text-[#C5922E]" strokeWidth={1.6} />
        </div>

        <h1
          className="text-[22px] font-bold text-[#1B2A4A] mb-2"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {t('accessExpired.title')}
        </h1>

        <p className="text-[13.5px] leading-relaxed text-[#6B7280] mb-1">
          {when ? t('accessExpired.bodyDated', { date: when }) : t('accessExpired.body')}
        </p>

        {user?.email && (
          <p className="text-[12px] text-[#6B7280]/80 mb-6" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {user.email}
          </p>
        )}

        <div className="flex items-center justify-center gap-2.5 flex-wrap">
          <a
            href="mailto:contact@stratmined.com?subject=Portal%20access%20renewal"
            className="h-[38px] px-4 flex items-center gap-2 rounded-lg bg-[#C5922E] text-white text-[12.5px] font-semibold shadow-[0_4px_12px_rgba(197,146,46,.24)] hover:bg-[#C8963E] transition-colors duration-150"
          >
            <Mail size={14} strokeWidth={1.8} />
            {t('accessExpired.contact')}
          </a>
          <button
            onClick={logout}
            className="h-[38px] px-4 flex items-center rounded-lg border border-[#1B2A4A] text-[#1B2A4A] text-[12.5px] font-semibold hover:bg-[#1B2A4A] hover:text-white transition-colors duration-150"
          >
            {t('common.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
};
